/**
 * 비용 EVM. 일정 EVM(기간 가중 EV/PV)에 승인 예산(BAC)과 실제 원가(AC)를 곱한다.
 * AC가 한 건도 없으면 지표는 모두 원가 없음. BAC가 없으면 비율·예측은 예산 없음.
 * 획득가치나 실제 원가가 0이면 비율·예측은 계산 없음.
 * 기준선이 없으면 PV는 없고, EV는 실제 진척에 예산을 곱한다.
 */
import type { CostActual, Project, Task } from "@/engine/types";
import type { EvmSummary } from "@/engine/health";
import { fmtCurrency } from "@/lib/format";

export interface CostEvm {
  /** 실제 원가를 한 번이라도 입력했으면 true. */
  available: boolean;
  bac: number;
  pv: number | null;
  ev: number | null;
  ac: number | null;
  cpi: number | null;
  cv: number | null;
  eac: number | null;
  etc: number | null;
  vac: number | null;
}

export const UNAVAILABLE_COST: CostEvm = {
  available: false,
  bac: 0,
  pv: null,
  ev: null,
  ac: null,
  cpi: null,
  cv: null,
  eac: null,
  etc: null,
  vac: null,
};

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

/** 작업 원가가 있는 작업은 기간 원가를 더하지 않는다. 없으면 null. */
export function collectActualCost(
  tasks: readonly Pick<Task, "id" | "actualCost">[],
  periods: readonly Pick<CostActual, "amount" | "taskId">[],
): number | null {
  const tasked = new Set<string>();
  let sum = 0;
  let any = false;
  for (const task of tasks) {
    if (typeof task.actualCost !== "number" || !Number.isFinite(task.actualCost)) continue;
    any = true;
    sum += task.actualCost;
    tasked.add(task.id);
  }
  for (const period of periods) {
    if (typeof period.amount !== "number" || !Number.isFinite(period.amount)) continue;
    if (period.taskId && tasked.has(period.taskId)) continue;
    any = true;
    sum += period.amount;
  }
  return any ? sum : null;
}

export function computeCostEvm(input: {
  bac: number;
  earnedRatio: number;
  plannedRatio: number | null;
  tasks: readonly Pick<Task, "id" | "actualCost">[];
  periods: readonly Pick<CostActual, "amount" | "taskId">[];
}): CostEvm {
  const bac = Number.isFinite(input.bac) ? input.bac : 0;
  const ac = collectActualCost(input.tasks, input.periods);
  if (ac == null) return { ...UNAVAILABLE_COST, bac };
  if (!(bac > 0)) {
    return { available: true, bac, pv: null, ev: null, ac, cpi: null, cv: null, eac: null, etc: null, vac: null };
  }
  const ev = bac * clamp01(input.earnedRatio);
  const pv = input.plannedRatio == null ? null : bac * clamp01(input.plannedRatio);
  const cv = ev - ac;
  const cpi = ac > 0 && ev > 0 ? ev / ac : null;
  const eac = cpi != null && cpi > 0 ? bac / cpi : null;
  const etc = eac == null ? null : eac - ac;
  const vac = eac == null ? null : bac - eac;
  return { available: true, bac, pv, ev, ac, cpi, cv, eac, etc, vac };
}

export function costEvmOf(
  project: Pick<Project, "budget" | "tasks" | "costActuals">,
  evm: Pick<EvmSummary, "ev" | "pv" | "hasBaseline">,
  /** 기준선이 없을 때 쓸 실제 진척(0–100). 기준선이 있으면 일정 EVM의 EV를 쓴다. */
  earnedPercent?: number,
): CostEvm {
  const earnedRatio = evm.hasBaseline ? evm.ev : clamp01((earnedPercent ?? 0) / 100);
  return computeCostEvm({
    bac: project.budget?.totalBudget ?? 0,
    earnedRatio,
    plannedRatio: evm.hasBaseline ? evm.pv : null,
    tasks: project.tasks ?? [],
    periods: project.costActuals ?? [],
  });
}

export const COST_KPI = [
  { key: "ev", label: "EV 획득가치" },
  { key: "ac", label: "AC 실제원가" },
  { key: "cpi", label: "CPI 비용성과" },
  { key: "cv", label: "CV 비용차이" },
  { key: "eac", label: "EAC 완료예상" },
  { key: "etc", label: "ETC 잔여예상" },
  { key: "vac", label: "VAC 완료차이" },
] as const;

export type CostKpiKey = (typeof COST_KPI)[number]["key"];

/** 값이 없는 이유. actual·budget만 입력 화면으로 잇는다. */
export type CostKpiGap = "actual" | "budget" | "ratio";

const GAP_LABEL: Record<CostKpiGap, string> = {
  actual: "원가 없음",
  budget: "예산 없음",
  ratio: "계산 없음",
};

export function costKpiGap(evm: CostEvm, key: CostKpiKey): CostKpiGap | null {
  if (!evm.available) return "actual";
  if (key === "ac") return null;
  if (!(evm.bac > 0)) return "budget";
  const value = key === "cpi" ? evm.cpi : evm[key];
  return typeof value === "number" ? null : "ratio";
}

function signedMoney(value: number, masked: boolean): string {
  const text = fmtCurrency(value, masked);
  if (masked || value <= 0) return text;
  return `+${text}`;
}

/** 화면·PDF가 같이 쓰는 표기. 값이 없으면 빠진 이유를 적는다. */
export function costKpiText(evm: CostEvm, key: CostKpiKey, masked = false): string {
  const gap = costKpiGap(evm, key);
  if (gap) return GAP_LABEL[gap];
  if (key === "cpi") return evm.cpi == null ? GAP_LABEL.ratio : evm.cpi.toFixed(2);
  const value = evm[key];
  if (typeof value !== "number") return GAP_LABEL.ratio;
  if (key === "cv" || key === "vac") return signedMoney(value, masked);
  return fmtCurrency(value, masked);
}
